import { useEffect, useMemo, useRef, useState } from "react";

import { useSelector } from "react-redux";

import { getAPIPayload } from "../../apis/getApi";
import { ROLES } from "../helpers/constants";
import {
  decryptPayload,
  findElevatedRiskUnlockedProperties,
  getObjLength,
  isMonitoringTierCustomer,
  normalizeSubscriptionCategory,
  resolveCustomerProfileCategory,
} from "../helpers/utils";

type BannerProperty = {
  id: number | string;
  category?: string | null;
};

export function useElevatedRiskMonitoringBanner() {
  const [loading, setLoading] = useState(true);
  const [atRiskProperties, setAtRiskProperties] = useState<BannerProperty[]>([]);
  const fallbackFetchStartedRef = useRef(false);

  const { userProfileDetails, loginAsSpouse: parentId } = useSelector(
    (state: any) => state?.userProfile || {},
  );
  const { subscriptionData } = useSelector((state: any) => state?.subscription || {});
  const { propertiesData, propertiesListLoaded } = useSelector(
    (state: any) => state?.properties || {},
  );

  const isSecondaryUser = Boolean(parentId);
  const subscriptionsLoaded = getObjLength(subscriptionData) > 0;
  const subscriptions =
    subscriptionData?.success && Array.isArray(subscriptionData?.data)
      ? subscriptionData.data
      : [];
  const effectiveProfileCategory = resolveCustomerProfileCategory({
    profileCategory: userProfileDetails?.data?.category,
    profileProductTier: userProfileDetails?.data?.product_tier,
    propertiesData,
  });

  const isMonitoringTier = useMemo(
    () =>
      isMonitoringTierCustomer({
        profileCategory: effectiveProfileCategory,
        subscriptions,
        subscriptionsLoaded,
      }) ||
      (Array.isArray(propertiesData) &&
        propertiesData.some(
          (property) =>
            property?.from_risk_measure ||
            normalizeSubscriptionCategory(property) === "basic_monitoring" ||
            normalizeSubscriptionCategory(property) === "enhanced_monitoring",
        )),
    [
      effectiveProfileCategory,
      propertiesData,
      subscriptions,
      subscriptionsLoaded,
    ],
  );

  const roles = decryptPayload(localStorage.getItem("roles")) || [];
  const isCustomer =
    roles.includes(ROLES.customer) || roles.includes(ROLES.secondaryUser);

  useEffect(() => {
    if (!isCustomer || !isMonitoringTier) {
      setAtRiskProperties([]);
      setLoading(false);
      fallbackFetchStartedRef.current = false;
      return;
    }

    if (propertiesListLoaded) {
      setAtRiskProperties(findElevatedRiskUnlockedProperties(propertiesData || []));
      setLoading(false);
      return;
    }

    if (fallbackFetchStartedRef.current) {
      return;
    }

    fallbackFetchStartedRef.current = true;
    let cancelled = false;

    const loadFallbackBannerData = async () => {
      setLoading(true);
      try {
        const url = isSecondaryUser
          ? `customer/properties-v1/${parentId}`
          : "customer/properties-v1";
        const response = await getAPIPayload({
          url,
          data: { limit: 100, offset: 0 },
          token: true,
        });

        if (cancelled) {
          return;
        }

        if (response?.success && Array.isArray(response.data)) {
          setAtRiskProperties(findElevatedRiskUnlockedProperties(response.data));
          return;
        }

        setAtRiskProperties(findElevatedRiskUnlockedProperties(propertiesData || []));
      } catch {
        if (!cancelled) {
          setAtRiskProperties(findElevatedRiskUnlockedProperties(propertiesData || []));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    const timer = window.setTimeout(() => {
      void loadFallbackBannerData();
    }, 2000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    isCustomer,
    isMonitoringTier,
    isSecondaryUser,
    parentId,
    propertiesData,
    propertiesListLoaded,
  ]);

  const firstAtRiskPropertyId = atRiskProperties[0]?.id ?? null;

  return {
    loading,
    showBanner: isMonitoringTier && atRiskProperties.length > 0,
    atRiskCount: atRiskProperties.length,
    firstAtRiskPropertyId,
  };
}

export default useElevatedRiskMonitoringBanner;
